import React, { createContext, useContext, useState, useEffect } from 'react';

// Import translation files
import enTranslations from '../locales/en.json';
import frTranslations from '../locales/fr.json';
import nlTranslations from '../locales/nl.json';
import deChTranslations from '../locales/de-ch.json';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [translations, setTranslations] = useState(enTranslations);

  // Available languages
  const languages = {
    en: {
      code: 'en',
      name: 'English',
      translations: enTranslations
    },
    fr: {
      code: 'fr',
      name: 'Français',
      translations: frTranslations
    },
    nl: {
      code: 'nl',
      name: 'Nederlands',
      translations: nlTranslations
    },
    'de-ch': {
      code: 'de-ch',
      name: 'Schweizerdeutsch',
      translations: deChTranslations
    }
  };

  // Load language from localStorage on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage && languages[savedLanguage]) {
      setCurrentLanguage(savedLanguage);
      setTranslations(languages[savedLanguage].translations);
    } else {
      // Try to detect browser language
      const browserLanguage = navigator.language.split('-')[0];
      if (languages[browserLanguage]) {
        setCurrentLanguage(browserLanguage);
        setTranslations(languages[browserLanguage].translations);
      }
    }
  }, []);

  // Change language function
  const changeLanguage = (languageCode) => {
    if (languages[languageCode]) {
      setCurrentLanguage(languageCode);
      setTranslations(languages[languageCode].translations);
      localStorage.setItem('language', languageCode);
    }
  };

  // Translation function
  const t = (key, fallback = '', variables = {}) => {
    const keys = key.split('.');
    let value = translations;
    
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        value = fallback || key;
        break;
      }
    }
    
    if (typeof value === 'string') {
      let result = value;
      Object.keys(variables).forEach(v => {
        result = result.replace(new RegExp(`{{${v}}}`, 'g'), variables[v]);
        result = result.replace(new RegExp(`{${v}}`, 'g'), variables[v]);
      });
      return result;
    }
    
    return fallback || key;
  };

  // Get current language info
  const getCurrentLanguageInfo = () => {
    return languages[currentLanguage];
  };

  // Get all available languages
  const getAvailableLanguages = () => {
    return Object.values(languages);
  };

  const value = {
    currentLanguage,
    translations,
    changeLanguage,
    t,
    getCurrentLanguageInfo,
    getAvailableLanguages,
    languages
  };

  return (
    <LanguageContext.Provider value={value}> 
      {children}
    </LanguageContext.Provider>
  );
};
 